/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.19.0
 * @since 1.19.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IRootReducer } from '../../reducers/rootReducer';
import { initWizardMultiSearchState } from './constant';
import { IWizardMultiSearchReducer } from './reducer';
import { IWizardMultiSearchStepConfirm, IWizardMultiSearchStepSelection, IWizardMultiSearchValidation } from './types';

export const makeSelectWizardMultiSearchState = (state: IRootReducer): IWizardMultiSearchReducer =>
    state.wizardMultiSearch || initWizardMultiSearchState;

export const makeSelectWizardMultiSearchValidation = (): OutputSelector<IRootReducer, IWizardMultiSearchValidation,
    (state: IWizardMultiSearchReducer) => IWizardMultiSearchValidation> =>
    createSelector(makeSelectWizardMultiSearchState, state => state.validation);

export const makeSelectWizardMultiSearchStepSelection = ()
    : OutputSelector<IRootReducer, IWizardMultiSearchStepSelection,
    (state: IWizardMultiSearchReducer) => IWizardMultiSearchStepSelection> =>
    createSelector(
        makeSelectWizardMultiSearchState,
        state => ({ templateUrl: state.templateUrl, maxRows: state.maxRows }),
    );

export const makeSelectWizardMultiSearchStepConfirm = ()
    : OutputSelector<IRootReducer, IWizardMultiSearchStepConfirm,
    (state: IWizardMultiSearchReducer) => IWizardMultiSearchStepConfirm> =>
    createSelector(
        makeSelectWizardMultiSearchState,
        state => ({ foundMsisdnsCount: state.foundMsisdnsCount, notFoundMsisdnsCount: state.notFoundMsisdnsCount }),
    );

export const makeSelectWizardMultiSearchFileName = (): OutputSelector<IRootReducer, string,
    (state: IWizardMultiSearchReducer) => string> =>
    createSelector(makeSelectWizardMultiSearchState, state => state.fileName);

export const makeSelectWizardMultiSearchIsFileLoading = (): OutputSelector<IRootReducer, boolean,
    (state: IWizardMultiSearchReducer) => boolean> =>
    createSelector(makeSelectWizardMultiSearchState, state => state.fileIsLoading);

export const makeSelectWizardMultiSearchFileError = (): OutputSelector<IRootReducer, string | undefined,
    (state: IWizardMultiSearchReducer) => string | undefined> =>
    createSelector(makeSelectWizardMultiSearchState, state => state.fileError);

export const makeSelectWizardMultiSearchSubscriberList = (isFound = true): OutputSelector<IRootReducer, string[],
    (state: IWizardMultiSearchReducer) => string[]> =>
    createSelector(makeSelectWizardMultiSearchState,
        state => isFound ? state.msisdn : state.msisdnNotFound);

export const makeSelectWizardMultiSearchSubscriberListHasMore = (isFound = true): OutputSelector<IRootReducer, boolean,
    (state: IWizardMultiSearchReducer) => boolean> =>
    createSelector(makeSelectWizardMultiSearchState,
        state => isFound ? state.msisdnHasMore : state.msisdnNotFoundHasMore);

export const makeSelectWizardMultiSearchSubscriberListLoading = (isFound = true): OutputSelector<IRootReducer, boolean,
    (state: IWizardMultiSearchReducer) => boolean> =>
    createSelector(makeSelectWizardMultiSearchState,
        state => isFound ? state.msisdnIsLoading : state.msisdnNotFoundIsLoading);

export const makeSelectWizardMultiSearchSubscriberListError = (isFound = true): OutputSelector<IRootReducer, string,
    (state: IWizardMultiSearchReducer) => string> =>
    createSelector(makeSelectWizardMultiSearchState,
        state => isFound ? state.msisdnError : state.msisdnNotFoundError);
